<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue';
import type { SocialLinks } from './SocialLinks.vue';
const nuxtApp = useNuxtApp();

const isSticky = ref(false);

const handleScroll = () => {
  isSticky.value = window.scrollY > 0;
};

onMounted(() => {
  handleScroll();
  window.addEventListener('scroll', handleScroll);
});
onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});

const burgerOpen = ref(false);

const toggleBurgerMenu = () => {
  burgerOpen.value = !burgerOpen.value;
};

const closeBurgerMenu = () => {
  setTimeout(() => {
    burgerOpen.value = false;
  }, 500);
};

onMounted(() => {
  nuxtApp.hook('page:finish', () => {
    closeBurgerMenu();
  });
});

const toggleDropdown = (item: MenuItem) => {
  item.open = !item.open;
};

export type MenuItem = {
  db_id: string;
  title: string;
  url: string;
  post_name: string;
  post_title: string;
  children?: MenuItem[];
  open?: boolean;
};

export type HeaderData = {
  options: {
    fields: {
      header_logo: {
        url: string;
        width?: number;
        height?: number;
        alt?: string;
      };
      header_sublogo: {
        url: string;
        width?: number;
        height?: number;
        alt?: string;
      };
      header_sublogo_url?: string;
      header_nav_link: {
        title: string;
        url: string;
        target?: string;
      };
      social_networks: SocialLinks;
    };
    menus: {
      primary: MenuItem[];
      secondary: MenuItem[];
    };
  };
};

defineProps<{
  header?: HeaderData;
}>();

const menuRef = ref<HTMLElement | null>(null);

const trapFocus = (event: KeyboardEvent) => {
  if (!burgerOpen.value || !menuRef.value) return;

  const focusableElements = menuRef.value.querySelectorAll<HTMLElement>(
    'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );

  if (focusableElements.length === 0) return;

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  if (event.key === 'Tab') {
    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }
};

watch(burgerOpen, async (newVal) => {
  if (newVal) {
    await nextTick();
    const firstFocusable = menuRef.value?.querySelector<HTMLElement>(
      'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    firstFocusable?.focus();
    document.addEventListener('keydown', trapFocus);
  } else {
    document.removeEventListener('keydown', trapFocus);
  }
});
</script>

<template>
  <header
    id="header"
    class="fixed top-0 z-90 w-full py-3 transition-all duration-300 lg:py-4.5"
    :class="{
      'border-b border-gray-400 bg-white': isSticky,
      'pt-6 lg:pt-9': !isSticky,
    }"
  >
    <span
      class="fixed inset-0 z-80 bg-cream/65 backdrop-blur transition-all duration-300 content-['']"
      :class="{
        'visible opacity-100': burgerOpen,
        'invisible opacity-0': !burgerOpen,
      }"
      @click="toggleBurgerMenu"
    />
    <div class="container-lg container flex">
      <Logo
        v-if="header?.options?.fields?.header_logo"
        :data="header?.options?.fields?.header_logo"
        :class="{
          'w-[198px] lg:w-[204px]': isSticky,
          'w-[228px] lg:w-[248px]': !isSticky,
        }"
      />
      <div class="hidden flex-1 justify-center self-center md:flex">
        <SubLogo
          v-if="header?.options?.fields?.header_sublogo"
          :data="header?.options?.fields?.header_sublogo"
          :href="header?.options?.fields?.header_sublogo_url"
          :class="{ 'lg:w-[204px]': isSticky }"
        />
      </div>
      <div class="ml-auto flex gap-4 md:ml-4 xl:ml-8 xl:gap-16">
        <ButtonLink
          v-if="header?.options?.fields?.header_nav_link"
          class="mt-2 hidden self-center lg:flex"
          :class="{ 'lg:mt-0 lg:text-md': isSticky }"
          v-bind="header?.options?.fields?.header_nav_link"
        />
        <div
          class="relative -my-3 flex py-3 pl-6 transition-all duration-300 md:pl-9 lg:-my-4.5 lg:py-4.5"
          :class="{
            'border-l border-gray-400': isSticky,
            '-mt-6 pt-7 lg:-mt-9 lg:pt-9': !isSticky,
          }"
        >
          <button
            class="relative block h-9 w-9 self-center hover:text-blue"
            :aria-expanded="burgerOpen"
            aria-label="Toggle navigation menu"
            @click="toggleBurgerMenu"
          >
            <span
              class="absolute left-0 top-1/2 -mt-2.25 w-full border-t border-current"
            />
            <span
              class="absolute left-0 top-1/2 w-full border-t border-current"
            />
            <span
              class="absolute left-0 top-1/2 mt-2.25 w-full border-t border-current"
            />
          </button>
        </div>
      </div>
    </div>
    <div
      ref="menuRef"
      class="fixed right-0 top-0 z-90 h-dvh w-full overflow-y-auto overscroll-none border-gray-400 bg-white p-6 py-8 transition-transform duration-500 sm:w-[499px] sm:border-l md:p-10"
      :class="{
        'menu-opened visible translate-x-0': burgerOpen,
        'invisible translate-x-full': !burgerOpen,
      }"
      role="navigation"
      aria-label="Mobile navigation menu"
    >
      <div class="mb-6 flex justify-between gap-4 sm:items-center">
        <strong class="subtitle pt-0.5 sm:pt-2">Menu</strong>
        <button
          class="relative h-9 w-9 self-center hover:text-blue"
          :aria-expanded="burgerOpen"
          aria-label="Close navigation menu"
          @click="toggleBurgerMenu"
        >
          <span
            class="-translate-1/2 absolute left-0 top-1/2 w-full rotate-45 border-t border-current"
          />
          <span
            class="-translate-1/2 absolute left-0 top-1/2 w-full -rotate-45 border-t border-current"
          />
        </button>
      </div>
      <nav class="mb-6">
        <ul
          v-if="header?.options?.menus?.primary"
          class="nav mb-7 border-t border-dotted border-current font-serif text-3xl"
        >
          <li
            v-for="item in header?.options?.menus?.primary"
            :key="item.db_id"
            class="border-b border-dotted border-current pb-2.5 pt-4.5"
          >
            <template v-if="item?.children?.length">
              <button
                class="flex w-full items-center justify-between gap-2 transition-all duration-300 hover:text-blue"
                :aria-expanded="item.open"
                :aria-controls="'dropdown-menu-' + item.db_id"
                :aria-label="'Toggle submenu for ' + item.title"
                @click.prevent="toggleDropdown(item)"
              >
                <span>{{ item.title }}</span>
                <svg
                  class="h-3 w-3 transition-all duration-300"
                  :class="{ '-rotate-180': !item.open }"
                >
                  <use href="/icons/icons.svg#arrow" />
                </svg>
              </button>
              <div
                class="drop grid font-sans text-xl transition-all duration-500 md:text-md"
                :class="{
                  'visible grid-rows-[1fr] pt-3': item.open,
                  'invisible grid-rows-[0fr]': !item.open,
                }"
              >
                <ul :id="'dropdown-menu-' + item.db_id" class="overflow-hidden">
                  <li
                    v-for="subItem in item.children"
                    :key="subItem.title"
                    class="mb-3 md:mb-1"
                  >
                    <NuxtLink
                      v-if="subItem?.url"
                      :to="subItem.url"
                      class="block decoration-1 underline-offset-2 hover:text-current hover:underline hover:decoration-dotted focus:text-blue focus:outline-none"
                    >
                      {{ subItem.title }}
                    </NuxtLink>
                  </li>
                </ul>
              </div>
            </template>
            <template v-else>
              <NuxtLink
                :to="item.url"
                class="block transition-all duration-300 hover:text-blue"
              >
                {{ item.title }}
              </NuxtLink>
            </template>
          </li>
        </ul>
        <ul
          v-if="header?.options?.menus?.secondary"
          class="sub-nav font-mono text-lg uppercase md:text-sm"
        >
          <li
            v-for="subNavItem in header?.options?.menus?.secondary"
            :key="subNavItem.db_id"
            class="mb-5 md:mb-2"
          >
            <NuxtLink
              :to="subNavItem.url"
              class="block decoration-1 underline-offset-2 hover:text-current hover:underline hover:decoration-dotted"
            >
              {{ subNavItem.title }}
            </NuxtLink>
          </li>
        </ul>
      </nav>
      <SocialLinks
        v-if="header?.options?.fields?.social_networks"
        :data="header?.options?.fields?.social_networks"
        type="type-cream"
        md
      />
    </div>
  </header>
</template>

<style scoped>
:global(body:has(.menu-opened)) {
  @apply h-full overflow-hidden;
}

:global(.nav a.router-link-active),
:global(.nav li:has(a.router-link-active) > button) {
  @apply text-blue;
}

:global(.sub-nav a.router-link-active),
:global(.nav [id*='dropdown-menu-'] a.router-link-active) {
  @apply text-current underline decoration-dotted decoration-1 underline-offset-2;
}
</style>
